import {
  computed,
  effect,
  Injectable,
  Signal,
  signal,
  WritableSignal,
  inject,
} from "@angular/core";
import { DoctorsApiService } from "../../api/users/doctors-api.service";
import { Observable, of, tap } from "rxjs";
import {
  CreateDoctorDto,
  FilterDoctorDto,
  ResponseDoctorDto,
  UpdateDoctorDto,
} from "../../../api/models";
import { CachedServiceBase } from "../../base/cached-service.base";

@Injectable({
  providedIn: "root",
})
export class CachedDoctorsService extends CachedServiceBase<ResponseDoctorDto> {
  private readonly _doctorsApiService = inject(DoctorsApiService);

  // =============================================================================
  // DB FILTERS (trigger backend calls)
  // =============================================================================
  /** Specialization filter - triggers DB call when changed */
  private readonly _specialization: WritableSignal<string | null> =
    signal(null);

  // =============================================================================
  // CLIENT-SIDE FILTERS (live filtering)
  // =============================================================================
  /** First name filter for live search */
  public readonly firstNameFilter: WritableSignal<string> = signal("");

  /** Last name filter for live search */
  public readonly lastNameFilter: WritableSignal<string> = signal("");

  // =============================================================================
  // CONSTRUCTOR & EFFECTS
  // =============================================================================
  constructor() {
    super();

    // Effect: reset pagination when filters change
    effect(() => {
      this.firstNameFilter();
      this.lastNameFilter();
      // Reset to first page when client filters change
      this.resetPagination();
    });
  }

  // =============================================================================
  // IMPLEMENT ABSTRACT METHODS
  // =============================================================================

  /**
   * Apply client-side filters to backend data
   */
  protected getFilteredData(): Signal<ResponseDoctorDto[]> {
    return computed(() => {
      const allData = this.data();
      const firstName = this.firstNameFilter().toLowerCase().trim();
      const lastName = this.lastNameFilter().toLowerCase().trim();

      if (!firstName && !lastName) return allData;

      return allData.filter(
        (item) =>
          (!firstName || item.firstName?.toLowerCase().includes(firstName)) &&
          (!lastName || item.lastName?.toLowerCase().includes(lastName)),
      );
    });
  }

  public refresh(): void {
    const specialization = this._specialization();
    if (specialization) {
      this._fetchFromBackend(specialization);
    }
  }

  public reset(): void {
    this._specialization.set(null);
    this.firstNameFilter.set("");
    this.lastNameFilter.set("");
    this.clearData();
  }

  // =============================================================================
  // COMPUTED SIGNALS
  // =============================================================================

  /** Signal to track if there are active text filters */
  public readonly hasActiveFilters: Signal<boolean> = computed(() => {
    const firstName = this.firstNameFilter().trim();
    const lastName = this.lastNameFilter().trim();
    return !!(firstName || lastName);
  });

  /** Filtered data based on client-side filters */
  public readonly filteredData: Signal<ResponseDoctorDto[]> =
    this.getFilteredData();

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  /**
   * Fetch data from backend based on specialization
   */
  private _fetchFromBackend(specialization: string | null): void {
    const filterData: Partial<FilterDoctorDto> = {};
    if (specialization)
      filterData.specialization = specialization as
        | 'PMA'
        | 'SOCIAL_FREEZING_FEMALE'
        | 'SOCIAL_FREEZING_MALE'
        | undefined;

    this._doctorsApiService
      .filter$(filterData)
      .pipe(tap((allResults) => this.updateData(allResults)))
      .subscribe();
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  /**
   * Set specialization and trigger backend call
   */
  public setSpecialization(specialization: string): void {
    this._specialization.set(specialization);
    this._fetchFromBackend(specialization);
  }

  /**
   * Clear text filters (firstName and lastName)
   */
  public clearTextFilters(): void {
    this.firstNameFilter.set("");
    this.lastNameFilter.set("");
  }

  /**
   * Legacy compatibility - reset error (not needed with new architecture)
   */
  public resetError(): void {
    // No-op - error handling is centralized now
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public nextPage(): void {
    this.goToNextPage();
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public previousPage(): void {
    this.goToPreviousPage();
  }

  /**
   * Legacy compatibility - get current page index
   */
  public currentPageIndex(): number {
    return this.paginationInfo().currentPage;
  }

  /**
   * Legacy compatibility - has next page
   */
  public hasNextPage(): boolean {
    return this.paginationInfo().hasNextPage;
  }

  /**
   * Legacy compatibility - has previous page
   */
  public hasPreviousPage(): boolean {
    return this.paginationInfo().hasPreviousPage;
  }

  /**
   * Legacy compatibility - should show pagination
   */
  public shouldShowPagination(): boolean {
    return this.paginationInfo().shouldShowPagination;
  }

  /**
   * Legacy compatibility - get total pages
   */
  public totalPages(): number {
    return this.paginationInfo().totalPages;
  }

  // =============================================================================
  // CRUD OPERATIONS
  // =============================================================================

  /**
   * Creates a new doctor
   * @param body The DTO containing doctor creation data
   * @returns Observable of the created doctor
   */
  public create$(body: CreateDoctorDto): Observable<ResponseDoctorDto> {
    return this._doctorsApiService
      .create$(body)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Updates an existing doctor
   * @param id The id of the doctor
   * @param body The DTO containing doctor update data
   * @returns Observable of the updated doctor
   */
  public update$(
    id: string,
    body: UpdateDoctorDto,
  ): Observable<ResponseDoctorDto> {
    return this._doctorsApiService
      .update$(id, body)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Deletes a doctor by id
   * @param id The id of the doctor
   * @returns Observable of operation result
   */
  public delete$(id: string): Observable<void> {
    return this._doctorsApiService.delete$(id).pipe(tap(() => this.refresh()));
  }

  /**
   * Retrieves a single doctor
   * @param id The id of the doctor to retrieve
   * @returns Observable of the doctor
   */
  public findOne$(id: string): Observable<ResponseDoctorDto> {
    // Search in backend data cache
    const allData = this.data();
    const cached = allData.find((doctor) => doctor.id === id);

    if (cached) {
      return of(cached);
    }

    return this._doctorsApiService.findOne$(id);
  }

  // =============================================================================
  // LEGACY COMPATIBILITY FOR EXISTING COMPONENTS
  // =============================================================================

  /**
   * Legacy compatibility - get all doctors (for backward compatibility)
   * @param forceRefresh Force refresh of cached data
   * @returns Observable of all doctors
   */
  public getDoctors$(
    forceRefresh: boolean = false,
  ): Observable<ResponseDoctorDto[]> {
    if (forceRefresh) {
      this.refresh();
    }
    return of(this.filteredData());
  }
}
